/**
 * Web Audio Recording Service Implementation
 *
 * Uses the MediaRecorder API for browser-based audio recording.
 * Supports Chrome (WebM), Safari (MP4), and Firefox (WebM).
 */

import {
  AudioFile,
  AudioRecordingService,
  RecordingState,
  PermissionDeniedError,
  InvalidRecordingStateError,
  RecordingStartError,
} from './audioRecordingService';

class WebAudioRecordingService implements AudioRecordingService {
  private mediaRecorder: MediaRecorder | null = null;
  private audioChunks: Blob[] = [];
  private recordingState: RecordingState = 'idle' as RecordingState;
  private startTime: number = 0;
  private pausedTime: number = 0;
  private totalPausedDuration: number = 0;
  private currentBlobUrl: string | null = null;
  private mediaStream: MediaStream | null = null;

  /**
   * Request microphone permission from the user
   */
  async requestPermission(): Promise<boolean> {
    try {
      // Check if getUserMedia is supported
      if (!navigator?.mediaDevices?.getUserMedia) {
        console.error('getUserMedia is not supported in this browser');
        return false;
      }

      // Request microphone access
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      // Stop the stream immediately (we just needed to check permission)
      stream.getTracks().forEach((track) => track.stop());

      return true;
    } catch (error) {
      console.error('Microphone permission denied:', error);
      return false;
    }
  }

  /**
   * Start a new audio recording
   */
  async startRecording(): Promise<void> {
    if (this.recordingState !== 'idle') {
      throw new InvalidRecordingStateError(
        `Cannot start recording from ${this.recordingState} state. Current state must be IDLE.`
      );
    }

    try {
      // Check if MediaRecorder is supported
      if (typeof MediaRecorder === 'undefined') {
        throw new RecordingStartError('MediaRecorder is not supported in this browser');
      }

      // Request microphone access
      this.mediaStream = await navigator.mediaDevices.getUserMedia({ audio: true });

      // Determine supported MIME type
      const mimeType = this.getSupportedMimeType();

      // Initialize MediaRecorder
      try {
        this.mediaRecorder = new MediaRecorder(this.mediaStream, { mimeType });
      } catch (error) {
        throw new RecordingStartError(
          `Failed to initialize MediaRecorder: ${error instanceof Error ? error.message : 'Unknown error'}`
        );
      }

      // Reset state
      this.audioChunks = [];
      this.startTime = Date.now();
      this.totalPausedDuration = 0;

      // Set up event handlers
      this.mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          this.audioChunks.push(event.data);
        }
      };

      this.mediaRecorder.onerror = (event) => {
        console.error('MediaRecorder error:', event);
        this.recordingState = 'idle' as RecordingState;
        this.cleanup();
      };

      // Start recording
      this.mediaRecorder.start();
      this.recordingState = 'recording' as RecordingState;
    } catch (error) {
      this.cleanup();

      if (error instanceof DOMException && error.name === 'NotAllowedError') {
        throw new PermissionDeniedError('Microphone permission denied by user');
      }

      if (error instanceof InvalidRecordingStateError || error instanceof RecordingStartError) {
        throw error;
      }

      throw new RecordingStartError(
        `Failed to start recording: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    }
  }

  /**
   * Pause the current recording
   */
  async pauseRecording(): Promise<void> {
    if (this.recordingState !== 'recording') {
      throw new InvalidRecordingStateError(
        `Cannot pause from ${this.recordingState} state. Must be RECORDING.`
      );
    }

    if (!this.mediaRecorder) {
      throw new InvalidRecordingStateError('No active MediaRecorder instance');
    }

    this.mediaRecorder.pause();
    this.pausedTime = Date.now();
    this.recordingState = 'paused' as RecordingState;
  }

  /**
   * Resume a paused recording
   */
  async resumeRecording(): Promise<void> {
    if (this.recordingState !== 'paused') {
      throw new InvalidRecordingStateError(
        `Cannot resume from ${this.recordingState} state. Must be PAUSED.`
      );
    }

    if (!this.mediaRecorder) {
      throw new InvalidRecordingStateError('No active MediaRecorder instance');
    }

    // Accumulate paused duration
    this.totalPausedDuration += Date.now() - this.pausedTime;

    this.mediaRecorder.resume();
    this.recordingState = 'recording' as RecordingState;
  }

  /**
   * Stop recording and save the audio file
   */
  async stopAndSave(): Promise<AudioFile> {
    if (
      this.recordingState !== 'recording' &&
      this.recordingState !== 'paused'
    ) {
      throw new InvalidRecordingStateError(
        `Cannot stop from ${this.recordingState} state. Must be RECORDING or PAUSED.`
      );
    }

    if (!this.mediaRecorder) {
      throw new InvalidRecordingStateError('No active MediaRecorder instance');
    }

    return new Promise((resolve, reject) => {
      if (!this.mediaRecorder) {
        reject(new InvalidRecordingStateError('No active MediaRecorder instance'));
        return;
      }

      this.mediaRecorder.onstop = () => {
        try {
          // Calculate final duration
          const endTime = Date.now();
          const duration = endTime - this.startTime - this.totalPausedDuration;

          // Create blob from chunks
          const mimeType = this.mediaRecorder?.mimeType || 'audio/webm';
          const audioBlob = new Blob(this.audioChunks, { type: mimeType });

          // Generate filename with timestamp
          const timestamp = this.formatTimestamp(new Date());
          const extension = this.getExtensionForMimeType(mimeType);
          const filename = `recording-${timestamp}.${extension}`;

          // Create File object from Blob for upload compatibility
          const audioFile = new File([audioBlob], filename, { type: mimeType });

          // Create object URL for playback/preview
          const blobUrl = URL.createObjectURL(audioBlob);

          // Store for cleanup
          if (this.currentBlobUrl) {
            URL.revokeObjectURL(this.currentBlobUrl);
          }
          this.currentBlobUrl = blobUrl;

          // Create AudioFile metadata with File object
          const audioFileMetadata: AudioFile = {
            uri: blobUrl,
            name: filename,
            size: audioBlob.size,
            mimeType: mimeType,
            duration: duration,
            createdAt: new Date().toISOString(),
            webFile: audioFile,
          };

          // Reset state
          this.recordingState = 'idle' as RecordingState;
          this.cleanup();

          resolve(audioFileMetadata);
        } catch (error) {
          this.recordingState = 'idle' as RecordingState;
          this.cleanup();
          reject(error);
        }
      };

      // Stop the recorder
      this.mediaRecorder.stop();
    });
  }

  /**
   * Discard the current recording and clean up resources
   */
  async discard(): Promise<void> {
    if (
      this.recordingState !== 'recording' &&
      this.recordingState !== 'paused'
    ) {
      throw new InvalidRecordingStateError(
        `Cannot discard from ${this.recordingState} state. Must be RECORDING or PAUSED.`
      );
    }

    if (this.mediaRecorder) {
      this.mediaRecorder.stop();
    }

    this.recordingState = 'idle' as RecordingState;
    this.cleanup();
  }

  /**
   * Get the current recording duration in milliseconds
   */
  getCurrentDuration(): number {
    if (this.recordingState === 'idle') {
      return 0;
    }

    if (this.recordingState === 'paused') {
      // Return duration at pause point
      return this.pausedTime - this.startTime - this.totalPausedDuration;
    }

    // RECORDING state - return current elapsed time
    return Date.now() - this.startTime - this.totalPausedDuration;
  }

  /**
   * Get the current recording state
   */
  getRecordingState(): RecordingState {
    return this.recordingState;
  }

  /**
   * Force cancel any active recording without throwing errors
   * Used for cleanup when navigating away or when app goes to background
   */
  async forceCancel(): Promise<void> {
    try {
      // Only attempt cleanup if not already idle
      if (this.recordingState === 'idle') {
        return;
      }

      // Stop MediaRecorder if it exists and is not already inactive
      if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
        this.mediaRecorder.stop();
      }

      // Reset state
      this.recordingState = 'idle' as RecordingState;
      this.cleanup();
    } catch (error) {
      // Never throw from forceCancel - just log and ensure cleanup
      console.warn('Error during forceCancel cleanup:', error);
      this.recordingState = 'idle' as RecordingState;
      this.cleanup();
    }
  }

  /**
   * Clean up resources
   */
  private cleanup(): void {
    // Stop media stream tracks
    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach((track) => track.stop());
      this.mediaStream = null;
    }

    // Clear recorder
    this.mediaRecorder = null;
    this.audioChunks = [];
  }

  /**
   * Get supported MIME type for the browser
   */
  private getSupportedMimeType(): string {
    // Check if isTypeSupported is available (some test environments may not have it)
    if (typeof MediaRecorder.isTypeSupported !== 'function') {
      return 'audio/webm'; // Default fallback
    }

    // Try WebM with Opus (Chrome, Firefox)
    if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) {
      return 'audio/webm;codecs=opus';
    }

    // Try WebM
    if (MediaRecorder.isTypeSupported('audio/webm')) {
      return 'audio/webm';
    }

    // Try MP4 (Safari)
    if (MediaRecorder.isTypeSupported('audio/mp4')) {
      return 'audio/mp4';
    }

    // Try Ogg
    if (MediaRecorder.isTypeSupported('audio/ogg;codecs=opus')) {
      return 'audio/ogg;codecs=opus';
    }

    // Fallback to default
    return 'audio/webm';
  }

  /**
   * Get file extension for MIME type
   */
  private getExtensionForMimeType(mimeType: string): string {
    if (mimeType.includes('webm')) return 'webm';
    if (mimeType.includes('mp4')) return 'mp4';
    if (mimeType.includes('ogg')) return 'ogg';
    return 'webm';
  }

  /**
   * Format timestamp for filename (YYYYMMDD-HHMMSS)
   */
  private formatTimestamp(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');

    return `${year}${month}${day}-${hours}${minutes}${seconds}`;
  }
}

// Export singleton instance
export const audioRecordingService = new WebAudioRecordingService();
